import { useState, useEffect, useCallback } from 'react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import {
  FiUser, FiUsers, FiBook, FiFileText, FiAlertTriangle,
  FiCheckCircle, FiClock, FiCalendar, FiActivity
} from 'react-icons/fi';
import api from '../api';

export default function TeacherMonitoring() {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [stats, setStats] = useState(null);
  const [issues, setIssues] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });

  useEffect(() => {
    loadTeachers();
  }, []);

  const loadTeacherStats = useCallback(async (teacherId) => {
    setLoadingStats(true);
    try {
      // Load teacher's classes
      const classesRes = await api.get('/api/class-groups');
      const teacherClasses = classesRes.data.classGroups?.filter(c =>
        c.teachers?.some(t => (t._id || t) === teacherId)
      ) || [];

      // Load lessons for this teacher
      const lessonsRes = await api.get('/api/lessons', {
        params: {
          teacherId,
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      });
      const teacherLessons = lessonsRes.data.lessons || [];

      // Calculate stats
      const totalStudents = teacherClasses.reduce((sum, c) => sum + (c.students?.length || 0), 0);
      const totalLessons = teacherLessons.length;

      // Check for issues
      const foundIssues = [];

      // Issue: Lessons not logged in recent days
      const today = new Date();
      const recentDays = 7;
      const recentLessons = teacherLessons.filter(l =>
        new Date(l.date) >= subDays(today, recentDays)
      );
      const expectedLessons = teacherClasses.length * recentDays;
      if (recentLessons.length < expectedLessons * 0.5) {
        foundIssues.push({
          type: 'lesson_backlog',
          message: `최근 ${recentDays}일간 수업일지 작성이 부족합니다 (${recentLessons.length}개)`,
          severity: 'warning'
        });
      }

      // Load attendance for absent students
      const attendanceRes = await api.get('/api/attendance', {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      });
      const absentRecords = (attendanceRes.data.records || []).filter(r =>
        r.status === 'absent' &&
        teacherClasses.some(c => c.students?.some(s => (s._id || s) === r.studentId?._id))
      );

      if (absentRecords.length > 0) {
        foundIssues.push({
          type: 'absent_students',
          message: `담당 학생 중 ${absentRecords.length}건의 결석 기록이 있습니다`,
          severity: 'info'
        });
      }

      setStats({
        classCount: teacherClasses.length,
        studentCount: totalStudents,
        lessonCount: totalLessons,
        recentLessonCount: recentLessons.length
      });
      setIssues(foundIssues);
      setLessons(teacherLessons.slice(0, 10));

    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    if (selectedTeacher) {
      loadTeacherStats(selectedTeacher._id);
    }
  }, [selectedTeacher, loadTeacherStats]);

  const loadTeachers = async () => {
    try {
      const res = await api.get('/api/users', { params: { role: 'teacher' } });
      setTeachers(res.data.users || []);
    } catch (error) {
      console.error('Failed to load teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="teacher-monitoring-page">
      <div className="page-header">
        <h2><FiActivity /> 강사 모니터링</h2>
      </div>

      <div className="monitoring-container">
        {/* Teacher List */}
        <div className="teacher-list-panel">
          <h3><FiUsers /> 강사 목록</h3>
          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : teachers.length === 0 ? (
            <div className="empty">등록된 강사가 없습니다</div>
          ) : (
            <div className="teacher-list">
              {teachers.map((teacher) => (
                <div
                  key={teacher._id}
                  className={`teacher-item ${selectedTeacher?._id === teacher._id ? 'active' : ''} ${!teacher.isActive ? 'inactive' : ''}`}
                  onClick={() => setSelectedTeacher(teacher)}
                >
                  <div className="teacher-avatar">
                    {teacher.name?.[0] || '?'}
                  </div>
                  <div className="teacher-info">
                    <span className="teacher-name">{teacher.name}</span>
                    <span className={`status ${teacher.isActive ? 'active' : 'inactive'}`}>
                      {teacher.isActive ? '활성' : '비활성'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats Panel */}
        <div className="stats-panel">
          {selectedTeacher ? (
            <>
              <div className="panel-header">
                <div className="teacher-detail">
                  <div className="avatar large">
                    {selectedTeacher.name?.[0] || '?'}
                  </div>
                  <div className="info">
                    <h3>{selectedTeacher.name}</h3>
                    <span>{selectedTeacher.email}</span>
                  </div>
                </div>
                <div className="date-filter">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  />
                  <span>~</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  />
                </div>
              </div>

              {loadingStats ? (
                <div className="loading">통계 로딩 중...</div>
              ) : stats ? (
                <>
                  {/* Stats Cards */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <FiBook className="stat-icon" />
                      <div className="stat-value">{stats.classCount}</div>
                      <div className="stat-label">담당 반</div>
                    </div>
                    <div className="stat-card">
                      <FiUsers className="stat-icon" />
                      <div className="stat-value">{stats.studentCount}</div>
                      <div className="stat-label">담당 학생</div>
                    </div>
                    <div className="stat-card">
                      <FiFileText className="stat-icon" />
                      <div className="stat-value">{stats.lessonCount}</div>
                      <div className="stat-label">수업 일지</div>
                    </div>
                    <div className="stat-card">
                      <FiClock className="stat-icon" />
                      <div className="stat-value">{stats.recentLessonCount}</div>
                      <div className="stat-label">최근 7일 일지</div>
                    </div>
                  </div>

                  {/* Issues */}
                  {issues.length > 0 && (
                    <div className="issues-section">
                      <h4><FiAlertTriangle /> 이슈</h4>
                      <div className="issues-list">
                        {issues.map((issue, i) => (
                          <div key={i} className={`issue-item ${issue.severity}`}>
                            {issue.severity === 'warning' ? (
                              <FiAlertTriangle className="issue-icon" />
                            ) : (
                              <FiCheckCircle className="issue-icon" />
                            )}
                            <span>{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Lessons */}
                  <div className="lessons-section">
                    <h4><FiFileText /> 최근 수업 일지</h4>
                    {lessons.length === 0 ? (
                      <div className="empty">작성된 일지가 없습니다</div>
                    ) : (
                      <div className="lesson-list">
                        {lessons.map((lesson) => (
                          <div key={lesson._id} className="lesson-item">
                            <div className="lesson-date">
                              <FiCalendar />
                              {format(new Date(lesson.date), 'MM/dd')}
                            </div>
                            <div className="lesson-info">
                              <span className="class-name">{lesson.classGroupId?.name || '-'}</span>
                              <span className="subject">{lesson.classGroupId?.subject || '-'}</span>
                            </div>
                            <div className="lesson-content">
                              {lesson.title || lesson.content?.substring(0, 50) || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="empty">통계를 불러올 수 없습니다</div>
              )}
            </>
          ) : (
            <div className="placeholder">
              <FiUser size={64} />
              <h3>강사를 선택하세요</h3>
              <p>왼쪽에서 모니터링할 강사를 선택하세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
